import { i18n } from '@fs/zion-locale'
import { useShowTemple, useCanAddPersonalData } from '@fs/zion-permissions'
import learnAboutYouCard from './images/learn-about-you-card.svg'
import saveFamilyMemsCard from './images/save-family-mems-card.svg'
import createTreeCard from './images/create-tree-card.svg'
import searchRecordsCard from './images/search-records-card.svg'
import tryIndexingCard from './images/try-indexing-card.svg'
import haveFunCard from './images/have-fun-card.svg'
import haveSomeoneInMindCard from './images/hsimCard.svg'
import ordinancesReadyCard from './images/ordinances-ready.svg'
import ancestorTipsCard from './images/ancestor-tips.svg'
import chineseGenBasicsCard from './images/chinese-gen-basics-card.webp'
import useAncestralCountries from './useAncestralCountries'

const chineseCountries = [
  'CN', // (Mainland) China
  'HK', // Hong Kong
  'MO', // Macau
  'SG', // Singapore
  'TW', // Taiwan
]

export default function usePrototypeCards() {
  const [canAddPersonalInformation] = useCanAddPersonalData()
  const [temple] = useShowTemple()
  const ancestralCountries = useAncestralCountries()

  const hasChineseAncestry =
    ancestralCountries &&
    typeof ancestralCountries === 'object' &&
    Object.keys(ancestralCountries).some((country) => chineseCountries.includes(country))

  const userIsChinese = i18n.language.startsWith('zh') || hasChineseAncestry

  const cards = [
    {
      src: createTreeCard,
      alt: '',
      title: i18n.t('build-family-tree.title', 'Build your family tree'),
      body: i18n.t(
        'build-family-tree.card-description',
        'Ready to start capturing your family history? A tree is a great way to start.'
      ),
      to: `/gettingstarted/create-family-tree`,
      linkName: 'GSCard: Build Your Family Tree',
      cardType: 'tree',
    },
    {
      src: searchRecordsCard,
      alt: '',
      title: i18n.t('search-historical-records.title', 'Find Ancestors in Records'),
      body: i18n.t(
        'search-historical-records.card-description',
        'Know which ancestor you are looking for? Get started with a targeted record search.'
      ),
      to: `/gettingstarted/find-ancestors`,
      linkName: 'GSCard: Search Historical Records',
      cardType: 'tree',
    },
    {
      src: learnAboutYouCard,
      alt: '',
      title: i18n.t('make-discoveries.title', 'Make Discoveries'),
      body: i18n.t('make-discoveries.card-description', 'Learn what other information we may have about your family.'),
      to: `/gettingstarted/make-discoveries`,
      linkName: 'GSCard: Make Discoveries',
      cardType: 'tree',
    },
    {
      src: chineseGenBasicsCard,
      alt: '',
      title: i18n.t('chinese-genealogy-basics.title', 'Tracing Your Chinese Roots'),
      body: i18n.t(
        'chinese-genealogy-basics.card-description',
        'Uncover your family history with expert tips and tools.'
      ),
      to: `/chinese/research/`,
      linkName: 'GSCard: Chinese Genealogy Basics',
      cardType: 'tree',
      requireChinese: true,
      external: true,
    },
    {
      src: saveFamilyMemsCard,
      alt: '',
      title: i18n.t('save-family-memories.title', 'Save Family Memories'),
      body: i18n.t(
        'save-family-memories.card-description',
        'Get started preserving your family photos and stories for generations to come.'
      ),
      to: `/gettingstarted/save-family-memories`,
      linkName: 'GSCard: Save Family Memories',
      cardType: 'experience',
      requirePersonalInformation: true,
    },
    {
      src: tryIndexingCard,
      alt: '',
      title: i18n.t('try-indexing.title', 'Get Involved'),
      body: i18n.t('try-indexing.card-description', 'You can help make more historical records available to search.'),
      to: '/gettingstarted/get-involved',
      linkName: 'GSCard: Try Indexing',
      excludeLangs: ['ko', 'mn', 'zh', 'zh-hans'],
      cardType: 'experience',
      requirePersonalInformation: true,
    },
    {
      src: haveFunCard,
      alt: '',
      title: i18n.t('have-fun.heading', 'Family History Fun'),
      body: i18n.t(
        'have-fun.card-description',
        'Choose from a list of activities that help put the fun into family history.'
      ),
      to: '/gettingstarted/family-history-fun',
      linkName: 'GSCard: Have Fun',
      cardType: 'experience',
      requirePersonalInformation: true,
    },
    {
      title: i18n.t('in-person-help.card.title', 'Get in-person help at a local center'),
      body: i18n.t(
        'in-person-help.card-description',
        'FamilySearch centers offer free, in-person guidance from an assistant trained to help you.'
      ),
      to: `/centers/about`,
      linkName: 'GSCard: Ordinances Ready',
      helpType: 'in-person',
      cardType: 'help',
      external: true,
    },
    {
      title: i18n.t('online-consultation.card.title', 'Schedule an online consultation'),
      body: i18n.t(
        'online-consultation.card-description',
        'Sign up for a free, 20-minute session with a research specialist.'
      ),
      to: `https://go.oncehub.com/ResearchStrategySession`,
      linkName: 'GSCard: Ordinances Ready',
      helpType: 'online-consult',
      cardType: 'help',
      external: true,
    },
    {
      title: i18n.t('help-center.card.title', 'Visit the FamilySearch Help Center'),
      body: i18n.t(
        'help-center.card-description',
        'Find answers to your research questions, view classes and videos, and discover much more.'
      ),

      to: `/help/helpcenter/landing/`,
      linkName: 'GSCard: Ordinances Ready',
      helpType: 'familysearch-center',
      cardType: 'help',
      external: true,
    },
  ]
  const memberCards = [
    {
      src: ordinancesReadyCard,
      alt: '',
      title: i18n.t('ordinance-ready.title', 'Ordinances Ready'),
      body: i18n.t(
        'ordinances-ready.card-description',
        'Find the names of ancestors and others awaiting ordinances that you can take to the temple.'
      ),
      to: '/gettingstarted/ordinances-ready',
      linkName: 'GSCard: Ordinances Ready',
      cardType: 'member',
    },
    {
      src: haveSomeoneInMindCard,
      alt: '',
      title: i18n.t('have-someone-in-mind.title', 'Have someone in mind?'),
      body: i18n.t(
        'have-someone-in-mind.card-description',
        'Perform ordinances, like baptism, in the temple for a deceased loved one.'
      ),
      to: '/gettingstarted/have-someone-in-mind',
      linkName: 'GSCard: Have someone in mind',
      cardType: 'member',
    },
    {
      src: ancestorTipsCard,
      alt: '',
      title: i18n.t('ancestor-search-tips.title', '3 ancestor search tips'),
      body: i18n.t(
        'ancestor-search-tips.card-description',
        'Try one of these simple activities to find ancestors awaiting temple ordinances.'
      ),
      to: `/gettingstarted/ancestor-search-tips`,
      linkName: 'GSCard: ancestor search tips',
      cardType: 'member',
    },
    ...cards,
  ]
  let returnedCards = temple ? memberCards : cards
  if (!userIsChinese) {
    returnedCards = returnedCards.filter((card) => !card.requireChinese)
  }
  if (!canAddPersonalInformation) {
    returnedCards = returnedCards.filter((card) => !card.requirePersonalInformation)
  }
  return returnedCards.filter((card) => !card.excludeLangs?.includes(i18n.language))
}
